export type DestinationCatalogStateLike = {
  sourceName?: string | null
  latitude?: number | string | null
  longitude?: number | string | null
  slug?: string | null
  status?: string | null
}

export type DestinationCityLike = {
  name?: string | null
  latitude?: number | string | null
  longitude?: number | string | null
}

export const normalizeDestinationCityName = (value = '') =>
  String(value || '')
    .split(',')[0]
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()

export const destinationDistanceKm = (
  latitudeA: number,
  longitudeA: number,
  latitudeB: number,
  longitudeB: number
) => {
  const toRadians = (value: number) => (value * Math.PI) / 180
  const latDelta = toRadians(latitudeB - latitudeA)
  const lonDelta = toRadians(longitudeB - longitudeA)
  const latA = toRadians(latitudeA)
  const latB = toRadians(latitudeB)
  const a =
    Math.sin(latDelta / 2) ** 2 +
    Math.cos(latA) * Math.cos(latB) * Math.sin(lonDelta / 2) ** 2

  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export const findDestinationCatalogState = <
  T extends DestinationCatalogStateLike
>(
  states: T[] = [],
  city: DestinationCityLike
): T | undefined => {
  const name = normalizeDestinationCityName(String(city?.name || ''))
  const latitude = Number(city?.latitude)
  const longitude = Number(city?.longitude)

  if (!name || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return undefined
  }

  return states
    .filter((state) => {
      const stateLatitude = Number(state?.latitude)
      const stateLongitude = Number(state?.longitude)

      return (
        normalizeDestinationCityName(String(state?.sourceName || '')) ===
          name &&
        Number.isFinite(stateLatitude) &&
        Number.isFinite(stateLongitude) &&
        destinationDistanceKm(
          stateLatitude,
          stateLongitude,
          latitude,
          longitude
        ) <= 50
      )
    })
    .sort((a, b) => {
      const statusA = a.status === 'active' ? 0 : 1
      const statusB = b.status === 'active' ? 0 : 1
      const slugA = String(a.slug || '')
      const slugB = String(b.slug || '')
      const suffixA = /-\d+$/.test(slugA) ? 1 : 0
      const suffixB = /-\d+$/.test(slugB) ? 1 : 0

      return (
        statusA - statusB ||
        suffixA - suffixB ||
        slugA.length - slugB.length ||
        slugA.localeCompare(slugB)
      )
    })[0]
}
